/**
 * BAIL OUT — AD-021's toggle, and the reason it is its own setting rather than
 * a row on any of the three panels that already exist.
 *
 * **The feature is a declared port enhancement.** Red Baron (1990) has no
 * bail-out and its own manual explains why: *"Aside from their balloon
 * observers, the Allies never issued these lifesaving devices to their
 * aviators"* (`docs/game/manual/02-historical-overview.md`). The user asked for
 * one (2026-08-27) on the same standing AD-018's burning buildings, AD-020's
 * building collisions and the dive whine ship on, and the full account is
 * `docs/engineering/bail-out-design.md`.
 *
 * ## Why not one of the existing homes
 *
 * - **Not the Realism Panel.** That screen is a pixel-exact reproduction of the
 *   original's own (`docs/game/ui-menus.md`); every rect on it is a recovered
 *   option and there is no spare one for a setting the original had no concept
 *   of. `effects-settings.ts` refuses the same panel for the same reason.
 * - **Not `effects-settings.ts`'s DISPLAY row.** That file is explicitly *"port
 *   extension **display** settings — visual effects the original never drew"*,
 *   and every one of its seven toggles adds a picture that nothing depends on.
 *   This is not a picture. It is a **gameplay verb** that changes whether a
 *   career ends, and filing it under DISPLAY would say it was cosmetic. (The
 *   section is also at its documented seven-row capacity, but that is the
 *   smaller reason and would not have been enough on its own.)
 * - **So: its own flag, surfaced in PREFERENCES' FLYING section**, beside the
 *   two other settings that are neither a picture nor a recovered realism word.
 *
 * ## On by default
 *
 * The same call AD-020 §5 makes and for its reason: a feature that ships
 * default-off is *"a feature invisible out of the box, which is exactly the
 * complaint that asked for it"*. A player who wants the 1918 Allied bargain
 * turns it off and the key does nothing at all — not a refusal message, not a
 * disabled hint row: with this off the verb does not exist.
 *
 * Same storage pattern as `stick-settings.ts`, which is the precedent for a
 * one-field settings group: a small `SettingsStore` round-trip, storage-agnostic
 * so it is testable without a real `localStorage`.
 */

import type { SettingsStore } from "../audio/settings.ts";

export const BAIL_SETTINGS_STORAGE_KEY = "red-opus/bail-settings";

export interface BailSettings {
  /** Whether the pilot may go over the side at all. Off means `H` is inert. */
  readonly enabled: boolean;
}

export const DEFAULT_BAIL_SETTINGS: BailSettings = { enabled: true };

/**
 * Reads whatever survives a `JSON.parse` of untrusted stored text.
 *
 * `!== false` rather than `=== true`, matching `normalizeEffectsSettings`: a
 * stored blob written before this setting existed has no member under the name,
 * and the default for a missing member is the default for the setting.
 */
export function normalizeBailSettings(candidate: unknown): BailSettings {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_BAIL_SETTINGS;
  const raw = candidate as Partial<Record<keyof BailSettings, unknown>>;
  return { enabled: raw.enabled !== false };
}

export function serializeBailSettings(settings: BailSettings): string {
  return JSON.stringify(settings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadBailSettings(store: SettingsStore): BailSettings {
  const raw = store.getItem(BAIL_SETTINGS_STORAGE_KEY);
  if (raw === null) return DEFAULT_BAIL_SETTINGS;
  try {
    return normalizeBailSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_BAIL_SETTINGS;
  }
}

export function saveBailSettings(store: SettingsStore, settings: BailSettings): void {
  store.setItem(BAIL_SETTINGS_STORAGE_KEY, serializeBailSettings(settings));
}
